import React from "react";
import { bool, func, object, string } from "prop-types";
import cx from "classnames";

import withId from "../../utils/withId";
import Toggle from "../../scripts/modules/Toggle";
import { Button, Icon } from "..";

import DropdownStatic from "./Dropdown.static";


const CLASS_ROOT = "dropdown";

class Dropdown extends React.Component {
  static displayName = "Dropdown";

  static propTypes = {
    /** Toggle button props */
    buttonProps: object,
    /** Custom dropdown id (automatically generated by default)  */
    id: string,
    /** Interactive dropdown does not close on click inside */
    interactive: bool,
    /** Sets dropdown open state */
    isOpen: bool,

    /** On dropdown close callback function. */
    onClose: func,
    /** On dropdown open callback function. */
    onOpen: func,
    /** <a href="https://popper.js.org/tooltip-documentation.html">Popper.js</a> options object */
    popperOptions: object,
    /**
     * Custom dropdown renderer. Passes props as function parameter.   Passed element must be forwarding ref similar to this, otherwise the JS module will fail to init.
     *
     * ```jsx
<Dropdown
  renderDropdown={dropdownProps =>
    React.createElement(
      React.forwardRef((forwardedProps, ref) => (
        <div
          ref={ref}
          className="do-not-create-trigger-from-div"
          {...forwardedProps}
        >
          This is a bad idea
        </div>
      )),
      dropdownProps
    )
  }
>
    * ```
    */
    renderDropdown: func,
    /**
     * Custom dropdown trigger renderer. Passes props as function parameter.   Passed element must be forwarding ref similar to this, otherwise dropdown will not attach to correct element.
     *
     * ```jsx
<Dropdown
  renderTrigger={triggerProps =>
    React.createElement(
      React.forwardRef((forwardedProps, ref) => (
        <div
          ref={ref}
          className="do-not-create-trigger-from-div"
          {...forwardedProps}
        >
          This is a bad idea
        </div>
      )),
      triggerProps
    )
  }
>
      * ```
     */
    renderTrigger: func,
  };

  static defaultProps = {
    popperOptions: {},
    onOpen: () => {},
    onClose: () => {},
  };

  constructor(props) {
    super(props);

    this.toggleButtonRef = React.createRef();
    this.dropdownRef = React.createRef();
  }

  componentDidMount() {
    const { popperOptions, onOpen, onClose, isOpen } = this.props;
    this.toggleButton = new Toggle(this.toggleButtonRef.current);
    this.dropdown = new DropdownStatic(this.dropdownRef.current, {
      anchorElement: this.toggleButtonRef.current,
      popperOptions,
      onOpen,
      onClose,
    });

    if (isOpen) {
      this.dropdown.open();
    }
  }

  componentDidUpdate({ isOpen }) {
    /* eslint-disable react/destructuring-assignment */
    if (isOpen !== this.props.isOpen) {
      if (this.props.isOpen) {
        this.dropdown.open();
      } else {
        this.dropdown.close();
      }
    }
    /* eslint-enable react/destructuring-assignment */
  }

  render() {
    const {
      children,
      className,
      buttonProps = {},
      interactive,
      popperOptions,
      renderTrigger,
      renderDropdown,
      id,
      isOpen,
      onClose,
      onOpen,
      ...other
    } = this.props;

    const classes = cx(CLASS_ROOT, className);

    const dataToggle = [
      {
        target: `#${id}`,
        attribute: "class",
        value: "is-active",
      },
      {
        target: "self",
        attribute: "aria-expanded",
      },
    ];

    const { children: buttonChildren, ...otherButtonProps } = buttonProps;

    const triggerToRender = (renderTrigger &&
      renderTrigger({
        "data-toggle": JSON.stringify(dataToggle),
        "aria-expanded": "false",
        "aria-controls": id,
        ref: this.toggleButtonRef,
        id: `${id}-trigger`,
      })) || (
      <Button
        data-toggle={JSON.stringify([
          ...dataToggle,
          {
            target: "self",
            attribute: "icon",
            value: "/sprite.svg#chevronup",
          },
        ])}
        aria-expanded="false"
        aria-haspopup="true"
        aria-controls={id}
        elemRef={this.toggleButtonRef}
        id={`${id}-trigger`}
        {...otherButtonProps}
      >
        {buttonChildren}
        <Icon name="chevrondown" className="icon-right" />
      </Button>
    );

    const dropdownToRender = (renderDropdown &&
      renderDropdown({
        id,
        ref: this.dropdownRef,
        "data-dropdown-interactive": interactive,
        "data-dropdown": "",
      })) || (
      <div
        id={id}
        role="menu"
        data-dropdown
        className={classes}
        ref={this.dropdownRef}
        data-dropdown-interactive={interactive}
        aria-labelledby={`${id}-trigger`}
        {...(isOpen ? { "data-dropdown-open-on-init": true } : {})}
        {...other}
      >
        {children}
      </div>
    );

    return (
      <>
        {triggerToRender}
        {dropdownToRender}
      </>
    );
  }
}

export default withId(Dropdown);
